import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { SectionHead, Reveal } from "./effects";

export const Showreel = () => {
  const [open, setOpen] = useState(false);

  return (
    <section id="showreel" data-testid="showreel-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          testId="showreel"
          eyebrow="Featured Showreel"
          title={
            <>
              Raw footage in. <span className="text-gradient-blue">Story out.</span>
            </>
          }
          sub="Watch a real before/after — unedited clip versus the final graded, sound-designed, retention-cut delivery."
        />

        <Reveal>
          <motion.div
            data-testid="showreel-player-card"
            whileHover={{ scale: 1.012 }}
            transition={{ type: "spring", stiffness: 160, damping: 20 }}
            onClick={() => setOpen(true)}
            className="group relative rounded-3xl overflow-hidden border border-white/10 cursor-pointer hover:border-[#D4AF37]/50 hover:shadow-[0_0_80px_rgba(212,175,55,0.25)] transition-all duration-500"
          >
            <div className="relative aspect-video overflow-hidden">
              <video
                src="/videos/raw-edited.mp4"
                poster="/thumbs/raw-edited.jpg"
                muted
                loop
                playsInline
                autoPlay
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1.2s] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  whileHover={{ scale: 1.12 }}
                  className="relative flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center rounded-full bg-[#D4AF37]/90 backdrop-blur shadow-[0_0_50px_rgba(212,175,55,0.6)] group-hover:shadow-[0_0_80px_rgba(212,175,55,0.9)] transition-shadow duration-500"
                  data-testid="showreel-play-btn"
                >
                  <Play size={28} className="text-white ml-1" fill="currentColor" />
                  <span className="absolute inset-0 rounded-full border border-[#F5DE81]/50 animate-ping" style={{ animationDuration: "2.2s" }} />
                </motion.div>
              </div>
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5DE81] mb-2">Raw vs Edited</div>
                  <div className="font-display text-xl sm:text-2xl font-semibold">The Transformation Reel</div>
                </div>
                <span className="glass rounded-full px-3.5 py-1.5 text-[11px] tracking-wider text-slate-300">0:22</span>
              </div>
            </div>
          </motion.div>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="showreel-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/95 backdrop-blur-xl p-4 sm:p-10"
            onClick={() => setOpen(false)}
          >
            <button
              data-testid="showreel-lightbox-close"
              aria-label="Close"
              className="absolute top-6 right-6 glass rounded-full p-3 text-white hover:border-[#D4AF37] transition-colors z-10"
            >
              <X size={20} />
            </button>
            <motion.div
              initial={{ scale: 0.85, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-5xl rounded-2xl overflow-hidden glow-border"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src="/videos/raw-edited.mp4"
                controls
                autoPlay
                playsInline
                className="w-full aspect-video bg-black"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
